import { Droplet, Flame, ShieldCheck, Leaf, Ban, Sprout, Sparkles } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Eyebrow, Reveal, SectionTitle } from "./Primitives";

const ICONS = { droplet: Droplet, flame: Flame, shield: ShieldCheck, leaf: Leaf, ban: Ban, sprout: Sprout };
const PATTERN = [2, 1, 1, 2, 2, 1];

// Zig-zag bento spans on a 3-col grid; the last item stretches so rows always close cleanly.
const spansFor = (count) => {
  const spans = [];
  let used = 0;
  for (let i = 0; i < count; i += 1) {
    let s = PATTERN[i % PATTERN.length];
    if (used % 3 + s > 3) s = 3 - (used % 3);
    if (i === count - 1) s = 3 - (used % 3);
    spans.push(s);
    used += s;
  }
  return spans;
};
const SPAN = { 1: "lg:col-span-1", 2: "lg:col-span-2", 3: "lg:col-span-3" };

export const Benefits = () => {
  const { content, t } = useSite();
  const list = (content.benefits || []).slice(0, 6);
  if (!list.length) return null;
  const spans = spansFor(list.length);
  const narrowCount = spans.filter((s) => s === 1).length;
  const lastNarrow = spans.lastIndexOf(1);
  return (
    <section id="benefits" data-testid="benefits-section" className="bg-bone py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mb-12 max-w-2xl">
          <Eyebrow>{t("benefits_eyebrow", { product: content.product?.name })}</Eyebrow>
          <SectionTitle>{t("benefits_title_a")} <em className="text-leaf">{t("benefits_title_b")}</em></SectionTitle>
        </Reveal>
        <div className="grid gap-4 sm:auto-rows-fr sm:grid-cols-2 lg:grid-cols-3">
          {list.map((b, i) => {
            const Icon = ICONS[b.icon] || Sparkles;
            const wide = spans[i] > 1;
            return (
              <Reveal key={b.title} delay={(i % 3) * 0.07} className={`${SPAN[spans[i]]} ${wide || (narrowCount % 2 && i === lastNarrow) ? "sm:col-span-2" : ""}`}>
                <article data-testid={`benefit-card-${i}`}
                  className={`group relative flex h-full min-h-[180px] flex-col justify-between overflow-hidden rounded-[28px] p-7 transition-transform duration-500 hover:-translate-y-1 sm:min-h-[220px] sm:p-9 ${wide ? "bg-forest text-bone" : "border border-forest/10 bg-white text-forest"}`}>
                  {wide && <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-leaf/40 blur-2xl transition-transform duration-700 group-hover:scale-125" aria-hidden />}
                  <div className="relative flex items-start justify-between">
                    <span className={`grid h-12 w-12 place-items-center rounded-full ${wide ? "bg-white/10 text-gold" : "bg-sand text-leaf"}`}>
                      <Icon className="h-5 w-5" aria-hidden />
                    </span>
                    <span className={`text-sm font-bold ${wide ? "text-white/75" : "text-sage"}`}>0{i + 1}</span>
                  </div>
                  <div className="relative mt-10">
                    <h3 className={`font-bold leading-tight tracking-[-0.01em] ${wide ? "text-[22px] sm:text-[26px]" : "text-[19px] sm:text-[22px]"}`}>{b.title}</h3>
                    <p className={`t-body mt-3 max-w-md ${wide ? "text-white/85" : "text-sage"}`}>{b.text}</p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
